import React from "react";
import type {
    PanelResizeLayout,
    PanelResizeProfile,
    PanelResizeStoreAdapter,
} from "./panelResize.types";
import { usePanelDragSession } from "./usePanelDragSession.hook";

export interface PanelResizeHandleProps {
    "profile":      PanelResizeProfile;
    "layoutBounds": PanelResizeLayout;
    "adapter":      PanelResizeStoreAdapter;
    "containerRef": React.RefObject<HTMLDivElement | null>;

    /** When set, adjusts grip contrast for light vs dark panel chrome. */
    "isDarkUi"?: boolean;

    /**
     * `overlay` spans the full chrome row width (itinerary details layout).
     * `inline` sits between side pills in a flex row.
     */
    "layout"?: "overlay" | "inline";
}

/**
 * Generic bottom-panel resize grip with keyboard accessibility.
 */
const PanelResizeHandle: React.FC<PanelResizeHandleProps> = ( {
    profile,
    layoutBounds,
    adapter,
    containerRef,
    isDarkUi = false,
    layout = "overlay",
} ) => {
    const { handleProps, isDragActive, handleRef } = usePanelDragSession( {
        profile,
        layoutBounds,
        adapter,
        containerRef,
    } );

    const containerClassName = layout === "inline"
        ? `relative w-full select-none h-10 px-[5px] z-0 transition-colors duration-200 ${
            isDragActive ? "cursor-grabbing" : "cursor-grab"
        }`
        : `absolute inset-x-0 top-[10px] h-10 -translate-y-1/2 select-none z-0 transition-colors duration-200 ${
            isDragActive ? "cursor-grabbing" : "cursor-grab"
        }`;

    const gripClassName = isDragActive
        ? "bg-blue-400"
        : isDarkUi
            ? "bg-white/55 hover:bg-white/70"
            : "bg-gray-400 hover:bg-gray-500";

    return (
        <div
            {...handleProps}
            className={containerClassName}
            ref={handleRef as React.Ref<HTMLDivElement>}
        >
            <div
                className={`pointer-events-none absolute left-1/2 top-1/2 h-1 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-200 ${ gripClassName }`}
            />
        </div>
    );
};

export default PanelResizeHandle;
